import React from 'react';
import {
  BILLING_PERIODS,
  BILLING_PERIOD_LABEL,
  BILLING_PERIOD_SUFFIX,
  fullPriceForPeriod,
  listPriceForPeriod,
  type BillingPrices,
} from '../../../lib/utils/billingPrices';

const fmt = (n: number) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number.isFinite(n) ? n : 0);

interface FrequencyPickerProps {
  /** Periodo attualmente scelto, in mesi. */
  value: number;
  onChange: (months: number) => void;
  /** Canone mensile del servizio, add-on esclusi. */
  monthlyBase: number;
  /** Regole di listino del servizio. */
  rules: BillingPrices;
  /** Totale degli add-on scelti per quel periodo, sconti loro compresi. */
  addonsForPeriod?: (months: number) => number;
  /**
   * Stessi add-on senza sconti per frequenza. Serve solo a calcolare la
   * percentuale di risparmio: sui servizi con base 0 lo sconto vive tutto negli
   * add-on, e confrontando i due totali la pillola resta veritiera.
   */
  addonsFullForPeriod?: (months: number) => number;
  /** Una tantum: il servizio non è ricorrente e la scelta va nascosta altrove. */
  periods?: readonly number[];
}

/**
 * Scelta della frequenza con il prezzo di ogni periodo già calcolato, sconto di
 * listino compreso. Si decide guardando i numeri invece di provare una
 * frequenza e tornare indietro.
 */
export function FrequencyPicker({
  value,
  onChange,
  monthlyBase,
  rules,
  addonsForPeriod,
  addonsFullForPeriod,
  periods = BILLING_PERIODS,
}: FrequencyPickerProps) {
  return (
    <div role="radiogroup" aria-label="Frequenza di fatturazione" className="flex flex-col gap-px py-1">
      {periods.map((months) => {
        const list = listPriceForPeriod(monthlyBase, rules, months);
        const addons = addonsForPeriod ? addonsForPeriod(months) : 0;
        const fullTotal =
          fullPriceForPeriod(monthlyBase, months) +
          (addonsFullForPeriod ? addonsFullForPeriod(months) : addons);
        const listTotal = list + addons;
        const rawSaving = fullTotal > 0 ? (1 - listTotal / fullTotal) * 100 : 0;
        const saving = rawSaving > 0.5 ? Math.round(rawSaving) : 0;
        const selected = months === value;

        return (
          <button
            key={months}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(months)}
            className={`flex items-center gap-2.5 px-2 py-1.5 rounded text-left transition-colors ${
              selected ? 'bg-[#2383E2]/[0.07]' : 'hover:bg-black/[0.03] dark:hover:bg-white/[0.04]'
            }`}
          >
            <span
              className={`w-3.5 h-3.5 rounded-full border-[1.5px] flex-shrink-0 ${
                selected
                  ? 'border-[#2383E2] bg-[#2383E2] shadow-[inset_0_0_0_2.5px_white] dark:shadow-[inset_0_0_0_2.5px_#111827]'
                  : 'border-black/20 dark:border-white/25'
              }`}
            />
            <span className="text-[13px] text-black dark:text-white">{BILLING_PERIOD_LABEL[months]}</span>
            <span className="ml-auto flex items-center gap-2 tabular-nums">
              {saving > 0 && (
                <span className="text-[11px] font-medium px-1.5 py-0.5 rounded text-[#0F7B6C] bg-[#0F7B6C]/10">
                  −{saving}% listino
                </span>
              )}
              <span className="text-[12px] text-black/55 dark:text-white/55">
                {fmt(listTotal)} {BILLING_PERIOD_SUFFIX[months]}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
